import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  Typography,
  Row,
  Col,
  Card,
  Divider,
  Tag,
  Empty,
  Space,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

import {
  Division,
  fetchDivisions,
  setFilter,
} from "./actions";

import {
  getFilteredDivisionList,
  getDivisionLoading,
  getFilterName,
} from "./selectors";

const { Title, Paragraph, Text } = Typography;

const guessMimeFromFilename = (filename?: string) => {
  const lower = (filename || "").toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".svg")) return "image/svg+xml";
  if (lower.endsWith(".webp")) return "image/webp";
  return "image/png";
};

const ensureDataUrl = (image?: string, contentType?: string, fileName?: string) => {
  if (!image) return undefined;
  if (image.startsWith("data:")) return image;
  const mime = contentType || guessMimeFromFilename(fileName);
  return `data:${mime};base64,${image}`;
};

const removeProtocol = (url: string): string => {
  return url.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
};

interface StateProps {
  loading: boolean;
  filtered: Division[];
  filterName: string;
}

interface DispatchProps {
  fetchList: () => void;
  setFilterAction: (value: string) => void;
}

type Props = StateProps & DispatchProps;

const DivisionManagerPage: React.FC<Props> = ({
  loading,
  filtered,
  filterName,
  fetchList,
  setFilterAction,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleAdd = () => {
    navigate("/division-manager/add");
  };

  const handleOpenEdit = (record: Division) => {
    navigate(`/division-manager/${record.id}`);
  };

  const cards = useMemo(() => {
    if (!filtered?.length) {
      return (
        <Col span={24}>
          <Empty description={loading ? "Loading…" : "No divisions found"} />
        </Col>
      );
    }

    return filtered.map((d) => {
      const website = (d.websiteUrl || "").trim();
      const websiteDisplay = website ? removeProtocol(website) : "";

      const addressLines = d.address
        ? [
            d.address.line1,
            d.address.line2,
            d.address.line3,
            d.address.line4,
            d.address.countryISOCode,
          ]
            .map((part) => (part ?? "").trim())
            .filter((part) => part.length > 0)
        : [];
      const addressDisplay = addressLines.join(", ");

      const cardBannerSrc = ensureDataUrl(
        d.accreditationBanner?.image,
        d.accreditationBanner?.contentType,
        d.accreditationBanner?.fileName
      );
      const fallback = "Not provided";

      return (
        <Col key={d.id} xs={24} sm={24} md={12} lg={8}>
          <Card
            hoverable
            onClick={() => handleOpenEdit(d)}
            style={{ height: "100%" }}
            styles={{ body: { minHeight: 200 } }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Title level={5} style={{ margin: 0 }}>
                {d.name}
              </Title>
              <Tag color={d.isActive ? "success" : "default"} style={{ borderRadius: 999 }}>
                {d.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div>
              <Text strong>Head Office Address</Text>
              <Paragraph style={{ marginTop: 6 }}>
                {addressDisplay || fallback}
              </Paragraph>
            </div>

            <div style={{ marginTop: 8 }}>
              <Text strong>Website Link</Text>
              <Paragraph style={{ marginTop: 6, wordBreak: "break-all" }}>
                {websiteDisplay ? (
                  <a href={website} target="_blank" rel="noopener noreferrer">
                    {websiteDisplay}
                  </a>
                ) : (
                  fallback
                )}
              </Paragraph>
            </div>

            <div style={{ marginTop: 8 }}>
              <Text strong>Accreditation Banner</Text>
              {cardBannerSrc ? (
                <div style={{ marginTop: 6 }}>
                  <img
                    src={cardBannerSrc}
                    alt="Accreditation banner"
                    style={{
                      width: 96,
                      height: 48,
                      objectFit: "cover",
                      borderRadius: 4,
                      border: "1px solid #f0f0f0",
                    }}
                  />
                </div>
              ) : (
                <Paragraph style={{ marginTop: 6 }}>{fallback}</Paragraph>
              )}
            </div>
          </Card>
        </Col>
      );
    });
  }, [filtered, loading]);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={3} style={{ margin: 5 }}>
          Division Manager
        </Title>

        <Space>
        <Input
          placeholder="Search by Division Name"
          allowClear
          value={filterName}
          onChange={(e) => setFilterAction(e.target.value)}
          style={{ width: 300, marginRight: 8 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add New Division
        </Button>
      </Space>
      </div>

      <Row gutter={[16, 16]}>
        {cards}
        <Col xs={24} sm={24} md={12} lg={8}>
          <Card
            hoverable
            onClick={handleAdd}
            style={{ height: "100%", borderStyle: "dashed" }}
            styles={{
                body: {
                  minHeight: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
              }}
          >
            <Space orientation="vertical" align="center">
              <Text strong> <PlusOutlined /> Add New Division</Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  loading: getDivisionLoading(state),
  filtered: getFilteredDivisionList(state),
  filterName: getFilterName(state),
});

const mapDispatchToProps: DispatchProps = {
  fetchList: fetchDivisions,
  setFilterAction: setFilter,
};

export default connect(mapStateToProps, mapDispatchToProps)(DivisionManagerPage);